import { createPortal } from 'react-dom'
import { Outlet } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import logo from '~/src/assets/br1_logo.webp'
import { s3 } from '~/utils/s3'

const VIDEO = s3('/video/signup')

/**
 * Backdrop for the auth pages (login, signup, activate, password reset).
 *
 * A looping video, with the 1080p cut served only to displays wide enough to
 * show it and the 720p one to everyone else -- the `media` attribute on the
 * first source is what makes that choice, so a phone never downloads the large
 * file. A newer single encode exists on the CDN
 * (br1-website-signup-video.mp4) but is 54MB against 10.6MB here, and pointing
 * one source at it would hand that to every phone on the signup page. Left on
 * the pair until a 720p companion cut exists.
 *
 * The three layers over it are not decoration. Two gradients darken the top and
 * bottom where the header and form sit, and the dot pattern breaks up the
 * footage so text stays legible against moving, high-contrast frames. Removing
 * the video without removing them leaves the panel muddy; removing them without
 * removing the video leaves the form hard to read. They belong together.
 *
 * `muted` is required for `autoPlay` to work at all in every current browser,
 * and `playsInline` stops iOS taking the video fullscreen on load.
 */
const Background = (props) => {
    const { className } = props
    const page = document.querySelector<HTMLElement>('#page-background')

    return (
        page &&
        createPortal(
            <div className={twMerge('', className)}>
                <video autoPlay muted loop playsInline className='object-cover w-full h-screen'>
                    <source src={`${VIDEO}.1080.mp4`} type='video/mp4' media='(min-width: 1920px)' />
                    <source src={`${VIDEO}.720.mp4`} type='video/mp4' />
                </video>

                <div className='absolute top-0 w-full h-[55%] bg-gradient-to-b from-black to-transparent'></div>
                <div className='absolute bottom-0 w-full h-[35%] bg-gradient-to-t from-black to-transparent'></div>
                <div className='absolute inset-0 bg-[image:radial-gradient(circle,black_0%,black_30%,transparent_30%)] bg-[size:2px_2px]'></div>
            </div>,
            page
        )
    )
}
export const LoginLayout = () => {
    return (
        <>
            <Background />
            <div className='min-h-[75vh]'>
                {/* In the layout rather than on each page: all six auth screens sit
                    under it -- login, signup, activate, password reset and set --
                    and repeating it per page is how they drift apart.

                    The wordmark is 686x180, so it is sized by height and left to
                    keep its own ratio; forcing a width would squash it. `webp`
                    over the `png` because it is 7KB against 41KB for the same
                    image, and this is the first thing on the page.

                    The space above it is MARGIN. It was padding, which the
                    border-box sizing puts inside the height, so `h-20 pt-16`
                    left the logo itself 16px tall on desktop. */}
                <img
                    src={logo}
                    alt='BR1'
                    className='mx-auto mt-10 h-16 w-auto sm:h-20 lg:mt-14 lg:h-24'
                />
                <Outlet />
            </div>
        </>
    )
}
